import { createUseStyles, useTheme } from 'react-jss'
import { useEffect } from 'react'
import Layout from 'components/common/Layout'
import { useParams } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Icon,
  Icons,
  Avatar,
  TypographyElement,
  MediaQuery,
  Skeleton
} from 'UI'
import Text from './text.json'
import style from './style'
import { amazonBucket } from 'constants/'
import Seo from './Seo'
import { stripHtml } from 'helpers'
import Description from 'components/common/Description'
import CoursesGridNew from 'components/common/CoursesGridNew'
import Expander from 'components/common/Expander'
import { CourseNeutralPath } from 'navigation/Routes'

const maxLength = 70

const RenderTrainerCounts = (value = ' ') => value >= 1000 ? `+${String(value).slice(0, -3)}K` : !value ? value : `+${value}`

const TrainerStatsCard = ({ value, label }) => {
  const useStyles = createUseStyles(style)
  const { cardClass } = useStyles({ loading: true })
  return (
    <div className={cardClass}>
      <TypographyElement variant='heading5'>{value}</TypographyElement>
      <TypographyElement variant='body1' spacing='8px 0 0 0'>{label}</TypographyElement>
    </div>
  )
}

const PageSkeleton = () => {
  const useStyles = createUseStyles(style)
  const { cardsContainer, avatarClass, rowClass, skeletonClass } = useStyles({ loading: true })
  return (
    <Container>
      <Row
        className={rowClass}
        justify='center'
        justifyMobile='center'
        justifyTablet='center'
      >
        <Col grid={12} align='mid' alignTablet='mid' alignMobile='mid'>
          <Avatar className={avatarClass} loading />
        </Col>
      </Row>
      <Row justify='center'>
        <Col grid={8} mgrid={12} sgrid={12} direction='column' start={3}>
          <div className={skeletonClass}>
            <Skeleton lines={1} width='250px' height='29px' margin='8px 0' wrapperWidth='250px' />
            <Skeleton lines={1} width='500px' height='29px' margin='8px 0' wrapperWidth='500px' />
            <div className={cardsContainer}>
              <Skeleton lines={1} width='154px' height='100px' wrapperWidth='154px' margin='8px 0' />
              <Skeleton lines={1} width='154px' height='100px' wrapperWidth='154px' margin='8px 0' />
              <Skeleton lines={1} width='154px' height='100px' wrapperWidth='154px' margin='8px 0' />
            </div>
            <Skeleton lines={2} width='100%' height='24px' margin='8px 0' wrapperWidth='500px' />
          </div>
        </Col>
        <Col grid={12} direction='column'>
          <Skeleton lines={1} width='80%' height='29px' margin='24px 0 16px 0' wrapperWidth='500px' />
          <CoursesGridNew loading />
        </Col>
      </Row>
    </Container>
  )
}

export default ({
  firstName,
  lastName,
  goToLink,
  avatar,
  coursesCount,
  studentsCount,
  reviewsCount,
  creationDate,
  courses = [],
  position,
  LinkedIn,
  FetchTrainer,
  description,
  clearTrainer,
  loading,
  metadataTitle,
  metadataDescription
}) => {
  const theme = useTheme()
  const formattedDescription = stripHtml(description)
  const { slug } = useParams()
  const useStyles = createUseStyles(style)

  const lastCourses = courses.filter(c => c.LastCourse)

  const lastCourseTag = lastCourses && lastCourses.length !== 0 ? [...new Set(lastCourses.reduce((a, b) => { b.Tags = [...a.Tags, ...b.Tags]; return b }).Tags)].join(', ') : ''

  const alt = `${firstName} ${lastName} ${lastCourseTag}`

  const {
    container,
    avatarClass,
    rowClass,
    linkedinUrlClass,
    linkedinIconClass,
    linkedinTextClass,
    cardsContainer,
    linkedinContainer
  } = useStyles({ loading })
  useEffect(() => {
    FetchTrainer(slug)
    return () => clearTrainer()
  }, [slug])
  const coursesWithTrainer = courses.map(course => ({ ...course, Author: `${firstName} ${lastName}` }))
  const showLinkedIn = LinkedIn && LinkedIn !== ''
  return (
    <Layout hideSupport>
      {loading
        ? null
        : (
          <Seo
            firstName={firstName}
            lastName={lastName}
            description={description}
            slug={slug}
            metadaDescription={metadataDescription}
            metadataTitle={metadataTitle}
            avatar={`${amazonBucket.bucketBaseUrl}${avatar}`}
          />
          )}
      {loading
        ? <PageSkeleton />
        : (
          <Container className={container}>
            <Row
              className={rowClass}
              justify='center'
              justifyMobile='center'
              justifyTablet='center'
            >
              <Col grid={12} align='mid' alignTablet='mid' alignMobile='mid'>
                <Avatar alt={alt} title={alt} imageAsBackground={false} className={avatarClass} img={`${amazonBucket.bucketBaseUrl}${avatar}`} loading={loading} key={loading} />
              </Col>
            </Row>
            <Row justify='center'>
              <Col grid={8} mgrid={12} sgrid={12} direction='column' start={3}>
                <>
                  <TypographyElement
                    align='center'
                    Component='h1'
                    variant='heading5'
                    spacing='16px 0 0 0'
                  >
                    {`${firstName} ${lastName}`}
                  </TypographyElement>
                  <TypographyElement
                    component='h1'
                    align='center'
                    variant='body1'
                    spacing='8px 0 12px 0'
                  >
                    {position}
                  </TypographyElement>
                  {
                    showLinkedIn
                      ? (
                        <div className={linkedinContainer} align='middle'>
                          <a className={linkedinUrlClass} target='_blank' href={LinkedIn} rel='noreferrer'>
                            <Icon style={linkedinIconClass} iconName={Icons.linkedin} />
                            <span className={linkedinTextClass}>Profil LinkedIn</span>
                          </a>
                        </div>
                        )
                      : null
                  }
                  <div className={cardsContainer}>
                    <TrainerStatsCard value={RenderTrainerCounts(studentsCount)} label={Text.students} />
                    <TrainerStatsCard value={coursesCount} label={Text.courses} />
                    <TrainerStatsCard value={RenderTrainerCounts(reviewsCount)} label={Text.reviews} />
                  </div>
                </>
              </Col>
              <Col grid={12} mgrid={12} sgrid={12}>
                <>
                  <MediaQuery HideOnMobile HideOnTablet>
                    {formattedDescription.length > 2 &&
                      <Description
                        noTitle
                        noExpand
                        Description={formattedDescription}
                        loading={loading}
                        descriptionTextHeight={maxLength}
                        showMoreTextProps={{ align: 'center', variant: 'button', fontWeight: 'bold', color: theme.primary[0], spacing: '21px 0 0 0', display: 'block' }}
                      />}
                  </MediaQuery>
                  <MediaQuery HideOnDesktop>
                    {formattedDescription && (
                      <Expander
                        noExpand
                        title={Text.about}
                        titleProps={{ variant: 'heading5', color: theme.primary[1000] }}
                        isOpen
                        marginBottom={32}
                        text={formattedDescription}
                        textProps={{ variant: 'body1', color: theme.neutral[100] }}
                        showMoreProps={{ variant: 'button', fontWeight: 'bold', color: theme.primary[0], spacing: '24px 0 0 0' }}
                      />
                    )}
                  </MediaQuery>
                </>
              </Col>
              <Col grid={12} direction='column'>
                <TypographyElement
                  component='h1'
                  variant='heading2'
                  variantMobile='heading4'
                  variantTablet='heading4'
                  Component='h2'
                  spacing='54px 0 16px 0'

                >{Text.trainerCoursesHeadline} {firstName} {lastName}
                </TypographyElement>
                <CoursesGridNew
                  loading={loading}
                  courses={coursesWithTrainer}
                  columnGap={43}
                  tabletColumnGap={24}
                  mobileColumnGap={0}
                  rowGap={24}
                  tabletRowGap={24}
                  mobileRowGap={24}
                  columnCount={4}
                  tabletColumnCount={2}
                  mobileColumnCount={1}
                  containerMarginBottom={32}
                  paginationCount={24}
                  containerMarginBottomTablet={32}
                  containerMarginBottomMobile={32}
                  activateLocalPagination
                  handleCardClick={(course) => { goToLink(`${CourseNeutralPath}/${course.Slug}`) }}
                />
              </Col>
            </Row>
          </Container>
          )}
    </Layout>
  )
}
